import type {MarkdownArtifactPanelData, MarkdownArtifactRevisionSummary} from '@reachinsight-app/shared/src/components/R3Chat/types'

export class MarkdownArtifactService {
  private readonly apiBaseUrl: string
  private readonly token: string | null

  constructor(apiBaseUrl: string, token: string | null) {
    this.apiBaseUrl = apiBaseUrl
    this.token = token
  }

  read(sessionId: string): Promise<MarkdownArtifactPanelData> {
    return this.get(`sessionId=${encodeURIComponent(sessionId)}`)
  }

  readRevision(sessionId: string, revision: number): Promise<MarkdownArtifactRevisionSummary & {content: string; previewHtml: string}> {
    return this.get(`sessionId=${encodeURIComponent(sessionId)}&revision=${revision}`)
  }

  compare(sessionId: string, fromRevision: number, toRevision: number): Promise<{
    from: MarkdownArtifactRevisionSummary
    to: MarkdownArtifactRevisionSummary
    difference: unknown
    fromHtml: string
    toHtml: string
  }> {
    return this.get(`sessionId=${encodeURIComponent(sessionId)}&compareFrom=${fromRevision}&compareTo=${toRevision}`)
  }

  approve(sessionId: string, changeSetId: string, summary: string, requestId: string = crypto.randomUUID()) {
    return this.mutate({action: 'approve', sessionId, changeSetId, summary}, requestId)
  }

  reject(sessionId: string, changeSetId: string, reason?: string, requestId: string = crypto.randomUUID()) {
    return this.mutate({action: 'reject', sessionId, changeSetId, ...(reason ? {reason} : {})}, requestId)
  }

  refine(sessionId: string, changeSetId: string, requestId: string = crypto.randomUUID()) {
    return this.mutate({action: 'refine', sessionId, changeSetId}, requestId)
  }

  restore(sessionId: string, sourceRevision: number, expectedHeadRevision: number, requestId: string = crypto.randomUUID()) {
    return this.mutate({action: 'restore', sessionId, sourceRevision, expectedHeadRevision}, requestId)
  }

  undo(sessionId: string, expectedHeadRevision: number, requestId: string = crypto.randomUUID()) {
    return this.mutate({action: 'undo', sessionId, expectedHeadRevision}, requestId)
  }

  async exportExact(sessionId: string, revision: number, format: string): Promise<{blob: Blob; fileName: string}> {
    const response = await fetch(`${this.apiBaseUrl}/api/r3/markdown-artifact?sessionId=${encodeURIComponent(sessionId)}&revision=${revision}&export=${encodeURIComponent(format)}`, {
      credentials: 'include', headers: this.headers(false), cache: 'no-store',
    })
    if (!response.ok) throw await responseError(response, 'Export failed')
    const disposition = response.headers.get('Content-Disposition') || ''
    return {blob: await response.blob(), fileName: disposition.match(/filename="([^"]+)"/)?.[1] || `artifact.${format}`}
  }

  async release(sessionId: string, revision: number, requestId: string = crypto.randomUUID()): Promise<never> {
    await this.mutate({action: 'release', sessionId, revision}, requestId)
    throw new Error('The authoritative publisher returned no release.')
  }

  private async get<T>(query: string): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}/api/r3/markdown-artifact?${query}`, {
      credentials: 'include', headers: this.headers(false), cache: 'no-store',
    })
    if (!response.ok) throw await responseError(response, 'Artifact request failed')
    return response.json() as Promise<T>
  }

  private async mutate(body: Record<string, unknown>, requestId: string): Promise<MarkdownArtifactPanelData> {
    const response = await fetch(`${this.apiBaseUrl}/api/r3/markdown-artifact`, {
      method: 'POST', credentials: 'include', headers: {...this.headers(true), 'Idempotency-Key': requestId}, body: JSON.stringify(body),
    })
    if (!response.ok) throw await responseError(response, 'Artifact action failed')
    return response.json() as Promise<MarkdownArtifactPanelData>
  }

  private headers(json: boolean): Record<string, string> {
    return {
      ...(json ? {'Content-Type': 'application/json'} : {}),
      ...(this.token ? {Authorization: `Bearer ${this.token}`} : {}),
    }
  }
}

async function responseError(response: Response, fallback: string): Promise<Error & {code?: string}> {
  const body = await response.json().catch(() => ({})) as {error?: string; code?: string}
  return Object.assign(new Error(body.error || `${fallback} (${response.status})`), {code: body.code})
}
